import React, { useState, useEffect, useContext } from 'react';
import { 
  Eye, 
  X, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Users, 
  TrendingUp,
  AlertTriangle,
  FileText,
  DollarSign,
  Calendar,
  Filter,
  Paperclip,
  Download,
  ExternalLink,
  Edit
} from 'lucide-react';
import { notification } from 'antd'; // Import Ant Design notification
import {ClaimPortalItems,ClaimPortalManagementItems} from "../data";
import Layout from "../components/Layout";
import { Sidebar } from "../components";
import AuthContext from "../contexts/AuthContext";
import Table from "../components/Table";

const API_BASE_URL = 'https://ess-api-django-a6fc2c26f42e.herokuapp.com/api/payroll/';

const AdminClaimManagement = () => {
  const { authTokens, user } = useContext(AuthContext);
  const [claims, setClaims] = useState([]);
  const [escalatedClaims, setEscalatedClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  
  // NEW: Payment date states
  const [tentativePaymentDate, setTentativePaymentDate] = useState('');
  const [showUpdatePaymentDateModal, setShowUpdatePaymentDateModal] = useState(false);
  const [updatePaymentDateLoading, setUpdatePaymentDateLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage] = useState(10);

  // Fetch claims on component mount
  useEffect(() => {
    fetchClaims();
    if (user.role === 'ADMIN' || user.email === 'jerson.antony@prowesstics.com') {
      fetchEscalatedClaims();
    }
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}claims/`, {
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
        },
      });
      const data = await response.json();
      if (data.status === 'success') {
        setClaims(data.data);
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalatedClaims = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}escalated-claims/`, {
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
        },
      });
      const data = await response.json();
      if (data.status === 'success') {
        setEscalatedClaims(data.data);
      }
    } catch (error) {
      console.error('Error fetching escalated claims:', error);
    }
  };

  const showConfirmation = (action, claim) => {
    setConfirmAction({ action, claim });
    setShowConfirmModal(true);
    // Reset payment date when opening approval modal
    if (action === 'approve') {
      setTentativePaymentDate('');
    }
  };

  const handleConfirm = async () => {
    // MANDATORY VALIDATION: Check if payment date is provided for approval
    if (confirmAction.action === 'approve' && !tentativePaymentDate) {
      notification.warning({
        message: 'Payment Date Required',
        description: 'Please select a tentative payment date before approving the claim.',
        placement: 'topRight',
        duration: 4
      });
      return;
    }

    if (confirmAction.action === 'approve') {
      await handleAction(confirmAction.claim.id, 'approve');
    } else if (confirmAction.action === 'close') {
      await handleAction(confirmAction.claim.id, 'close');
    } else if (confirmAction.action === 'reject') {
      await handleReject();
    }
  };

  const handleAction = async (claimId, action) => {
    try {
      const requestBody = { action };
      
      // Add tentative payment date if approving (now mandatory)
      if (action === 'approve') {
        if (!tentativePaymentDate) {
          notification.error({
            message: 'Payment Date Required',
            description: 'Payment date is mandatory for approval',
            placement: 'topRight',
            duration: 4
          });
          return;
        }
        requestBody.tentative_payment_date = tentativePaymentDate;
      }

      const response = await fetch(`${API_BASE_URL}claims/${claimId}/action/`, {
        method: 'POST',
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.status === 'success') {
        setApprovalSuccess(true);
        await fetchClaims();
        if (user.role === 'ADMIN' || user.email === 'jerson.antony@prowesstics.com') {
          await fetchEscalatedClaims();
        }
      } else {
        notification.error({
          message: 'Action Failed',
          description: data.message || 'Error performing action',
          placement: 'topRight',
          duration: 4
        });
        setApprovalSuccess(false);
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      setApprovalSuccess(false);
      await fetchClaims();
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      notification.warning({
        message: 'Rejection Reason Required',
        description: 'Please provide a rejection reason',
        placement: 'topRight',
        duration: 3
      });
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}claims/${selectedClaim.id}/action/`, {
        method: 'POST',
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          action: 'reject',
          rejection_reason: rejectionReason 
        }),
      });

      const data = await response.json();
      if (data.status === 'success') {
        setShowRejectModal(false);
        setShowConfirmModal(false);
        setRejectionReason('');
        setConfirmAction(null);
        notification.success({
          message: 'Claim Rejected',
          description: 'Claim rejected successfully!',
          placement: 'topRight',
          duration: 3
        });
        await fetchClaims();
      }
    } catch (error) {
      console.error('Error rejecting claim:', error);
      notification.error({
        message: 'Rejection Failed',
        description: 'Error rejecting claim. Please try again.',
        placement: 'topRight',
        duration: 4
      });
      await fetchClaims();
    }
  };

  // NEW: Handle payment date update
  const handleUpdatePaymentDate = async () => {
    if (!tentativePaymentDate) {
      notification.warning({
        message: 'Date Required',
        description: 'Please select a payment date',
        placement: 'topRight',
        duration: 3
      });
      return;
    }

    setUpdatePaymentDateLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}claims/${selectedClaim.id}/update-payment-date/`,
        {
          method: 'POST',
          headers: {
            Authorization: "Bearer " + String(authTokens.access),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tentative_payment_date: tentativePaymentDate,
          }),
        }
      );

      const data = await response.json();
      if (data.status === 'success') {
        notification.success({
          message: 'Payment Date Updated',
          description: 'Payment date updated successfully! Email notification sent to employee.',
          placement: 'topRight',
          duration: 4
        });
        setShowUpdatePaymentDateModal(false);
        setTentativePaymentDate('');
        await fetchClaims();
        // Update selected claim if details modal is open
        if (showDetails) {
          setSelectedClaim(data.data);
        }
      } else {
        notification.error({
          message: 'Update Failed',
          description: data.message || 'Error updating payment date',
          placement: 'topRight',
          duration: 4
        });
      }
    } catch (error) {
      console.error('Error updating payment date:', error);
      notification.error({
        message: 'Update Failed',
        description: 'Error updating payment date. Please try again.',
        placement: 'topRight',
        duration: 4
      });
    } finally {
      setUpdatePaymentDateLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      'APPROVED': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      'REJECTED': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      'CLOSED': { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig['PENDING'];
    const IconComponent = config.icon;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${config.bg} ${config.text}`}>
        <IconComponent size={12} />
        {status}
      </span>
    );
  };

  const [sortConfig, setSortConfig] = useState({ key: "null", direction: "asc" });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedClaims = React.useMemo(() => {
    let sortable = [...claims];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [claims, sortConfig]);

  const filteredClaims = sortedClaims.filter(claim => {
    if (activeTab === 'pending') return claim.status === 'PENDING';
    if (activeTab === 'escalated') return escalatedClaims.some(esc => esc.claim_request.id === claim.id);
    if (filterStatus !== 'all') return claim.status === filterStatus;
    return true;
  });

  const stats = {
    total: claims.length,
    pending: claims.filter(c => c.status === 'PENDING').length,
    approved: claims.filter(c => c.status === 'APPROVED').length,
    rejected: claims.filter(c => c.status === 'REJECTED').length,
    closed: claims.filter(c => c.status === 'CLOSED').length,
    escalated: escalatedClaims.length,
    totalAmount: claims
      .filter(c => c.status === 'PENDING')
      .reduce((sum, c) => {
        const amount = Number(c.amount) || 0;
        return sum + amount;
      }, 0)
  };

  // Allow Jerson, HR, and RMG to approve/reject and manage payment dates
  const canApproveReject = user.email === 'jerson.antony@prowesstics.com' || 
                           user.role === 'HR' || 
                           user.role === 'RMG' ||
                           user.user_role === 'HR' ||
                           user.user_role === 'RMG';

  // Handle view claim details
  const handleViewClaim = (rowIndex) => {
    const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
    setSelectedClaim(filteredClaims[claimIndex]);
    setShowDetails(true);
  };

  // Handle approve claim
  const handleApproveFromTable = (rowIndex) => {
    const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
    const claim = filteredClaims[claimIndex];
    if (claim.status === 'PENDING' && canApproveReject) {
      showConfirmation('approve', claim);
    }
  };

  // Handle reject claim
  const handleRejectFromTable = (rowIndex) => {
    const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
    const claim = filteredClaims[claimIndex];
    if (claim.status === 'PENDING' && canApproveReject) {
      setSelectedClaim(claim);
      setShowRejectModal(true);
    }
  };

  // File download/view handler
  const handleFileAction = (fileUrl, fileName = 'attachment') => {
    if (fileUrl) {
      if (fileUrl.toLowerCase().includes('.pdf') || 
          fileUrl.toLowerCase().includes('.jpg') || 
          fileUrl.toLowerCase().includes('.jpeg') || 
          fileUrl.toLowerCase().includes('.png')) {
        window.open(fileUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  // Prepare table data
  const tableHeader = [
    { label: "Employee", key: "employee_name" },
    { label: "Purpose", key: "purpose" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status" },
    { label: "Date", key: "created_at" },
    { label: "Actions", key: "null" }
  ];

  const tableBody = filteredClaims.map(claim => ({
    employee: 
    (
      <div className="flex items-center">
        <div className="flex-shrink-0 h-8 w-8">
          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
            <Users size={16} className="text-gray-600" />
          </div>
        </div>
        <div className="ml-3">
          <div className="text-sm font-medium text-gray-900">{claim.employee_name}</div>
          <div className="text-sm text-gray-500">{claim.employee_email}</div>
        </div>
      </div>
    ),
    purpose: (
      <div>
        <div className="text-sm font-medium text-gray-900">{claim.purpose}</div>
        <div className="text-sm text-gray-500 max-w-xs truncate">{claim.one_line_description}</div>
        {claim.attachment && (
          <div className="flex items-center gap-1 mt-1">
            <Paperclip size={12} className="text-blue-500" />
            <span className="text-xs text-blue-500">Has attachment</span>
          </div>
        )}
        {claim.tentative_payment_date && (
          <div className="flex items-center gap-1 mt-1">
            <Calendar size={12} className="text-green-500" />
            <span className="text-xs text-green-600">
              Pay: {new Date(claim.tentative_payment_date).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>
    ),
    
    amount: `₹${Number(claim.amount || 0).toLocaleString("en-IN")}`,
    status: getStatusBadge(claim.status),
    date: new Date(claim.created_at).toLocaleDateString()
  }));

  // Check if claim can be approved/rejected
  const canApproveRejectClaim = (rowIndex) => {
    const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
    return filteredClaims[claimIndex]?.status === 'PENDING' && canApproveReject;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Get today's date in YYYY-MM-DD format for min date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <Layout sidebar={<Sidebar sidebarLinks={ClaimPortalManagementItems} />}>
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Claim Management Portal</h1>
          <p className="text-gray-600">Manage and review employee claim requests</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Claims</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 bg-gray-100 rounded-full">
                <XCircle className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Closed</p>
                <p className="text-2xl font-bold text-gray-900">{stats.closed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Escalated</p>
                <p className="text-2xl font-bold text-gray-900">{stats.escalated}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{Number(stats.totalAmount).toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex space-x-1">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'all' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  All Claims
                </button>
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'pending' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Pending ({stats.pending})
                </button>
                {stats.escalated > 0 && (
                  <button
                    onClick={() => setActiveTab('escalated')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'escalated' 
                        ? 'bg-red-100 text-red-700' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Escalated ({stats.escalated})
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Claims Table */}
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading claims...</p>
            </div>
          ) : filteredClaims.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No claims found for the selected filters.</p>
            </div>
          ) : (
            <div className="transition flex flex-col items-center justify-center w-full pb-4 mb-4 mt-4">
              <div className="w-full intro-x bg-blue-900 flex rounded-t-3xl justify-center items-center py-3">
                <h4 className="mx-6 text-center table-header-text font-bold text-sm md:text-lg text-white">
                  Claim Management
                </h4>
              </div>
              <Table
                header={tableHeader}
                body={tableBody}
                btnTitle="View"
                onClick={handleViewClaim}
                btnTitleTwo={(rowIndex) => {
                  const claim = filteredClaims[(currentPage - 1) * rowPerPage + rowIndex];
                  return claim.status === "APPROVED" ? "Close" : claim.status === "CLOSED" ? "Closed" : claim.status === "REJECTED" ? "Closed":"Approve";
                }}
                btnTitleTwoName={true}
                onClickTwo={(rowIndex) => {
                  const claim = filteredClaims[(currentPage - 1) * rowPerPage + rowIndex];
                  if (claim.status === "PENDING") {
                    showConfirmation('approve', claim);
                  } else if (claim.status === "APPROVED") {
                    showConfirmation('close', claim);
                  }
                }}
                btnTitleThree="Reject"
                onClickThree={handleRejectFromTable}
                buttonStyleTwo={(rowIndex) => !canApproveRejectClaim(rowIndex)}
                btndisTwo={(rowIndex) => !canApproveRejectClaim(rowIndex)}
                btndisThree={(rowIndex) => !canApproveRejectClaim(rowIndex)}
                currentPage={currentPage}
                rowPerPage={rowPerPage}
                paginationNeed={true}
                paginationBody={filteredClaims}
                onPageChange={handlePageChange}
                onSort={handleSort}
                sortConfig={sortConfig}
              />
            </div>
          )}
        </div>
        
        {/* Confirmation Modal */}
        {showConfirmModal && confirmAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
              
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-900 to-blue-900 px-6 py-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {approvalSuccess
                        ? "Success"
                        : confirmAction.action === "approve"
                        ? "Confirm Approval"
                        : "Confirm Close"}
                    </h2>
                    {!approvalSuccess && (
                      <p className="text-blue-100 text-sm mt-1">
                        This action cannot be undone
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowConfirmModal(false);
                      setApprovalSuccess(false);
                      setConfirmAction(null);
                      setTentativePaymentDate('');
                    }}
                    className="text-blue-100 hover:text-white transition-colors p-1"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                {approvalSuccess ? (
                  // ✅ Success UI
                  <div className="flex flex-col items-center text-center">
                    <CheckCircle className="h-12 w-12 text-green-600 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {confirmAction.action === "approve"
                        ? "Claim approved successfully!"
                        : "Claim closed successfully!"}
                    </h3>
                    {confirmAction.action === "approve" && tentativePaymentDate && (
                      <p className="text-sm text-gray-600 mb-2">
                        Email notification sent with payment date: {new Date(tentativePaymentDate).toLocaleDateString()}
                      </p>
                    )}
                    <button
                      onClick={() => {
                        setShowConfirmModal(false);
                        setApprovalSuccess(false);
                        setConfirmAction(null);
                        setTentativePaymentDate('');
                      }}
                      className="mt-4 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-semibold transition-all"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  // ❓ Confirmation UI
                  <>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-blue-100 rounded-full">
                        <CheckCircle className="h-8 w-8 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {confirmAction.action === "approve"
                            ? "Approve Claim Request?"
                            : "Close Claim Request?"}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          You are about to {confirmAction.action} the claim for "
                          {confirmAction.claim.purpose}"
                        </p>
                        {confirmAction.action === "close" && (
                          <p className="text-red-600 text-sm font-semibold mt-2">
                            ⚠️ Are you sure the payment is done before closing the claim?
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Employee:</span>
                          <p className="font-medium">{confirmAction.claim.employee?.name || confirmAction.claim.employee_name}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Amount:</span>
                          <p className="font-medium text-blue-600">
                            ₹{Number(confirmAction.claim.amount || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Date Input (MANDATORY for approve action) */}
                    {confirmAction.action === "approve" && (
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <span className="flex items-center gap-2">
                            <Calendar size={16} />
                            Tentative Payment Date <span className="text-red-500">*</span>
                          </span>
                        </label>
                        <input
                          type="date"
                          value={tentativePaymentDate}
                          onChange={(e) => setTentativePaymentDate(e.target.value)}
                          min={getTodayDate()}
                          required
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                            !tentativePaymentDate 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-blue-500'
                          }`}
                        />
                        <p className="text-xs text-red-600 mt-1 font-medium">
                          ⚠️ Payment date is required. An email will be sent to the employee with the expected payment date.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={handleConfirm}
                        disabled={confirmAction.action === "approve" && !tentativePaymentDate}
                        className={`flex-1 py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                          confirmAction.action === "approve" && !tentativePaymentDate
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                        }`}
                      >
                        <CheckCircle size={16} />
                        Yes, {confirmAction.action === "approve" ? "Approve" : "Close"}
                      </button>
                      <button
                        onClick={() => {
                          setShowConfirmModal(false);
                          setConfirmAction(null);
                          setTentativePaymentDate('');
                        }}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
                      >
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Update Payment Date Modal */}
        {showUpdatePaymentDateModal && selectedClaim && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-white">Update Payment Date</h2>
                    <p className="text-blue-100 text-sm mt-1">Set new tentative payment date</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUpdatePaymentDateModal(false);
                      setTentativePaymentDate('');
                    }}
                    className="text-blue-100 hover:text-white transition-colors p-1"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Claim:</span> {selectedClaim.purpose}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Amount:</span> ₹{Number(selectedClaim.amount).toLocaleString()}
                    </p>
                    {selectedClaim.tentative_payment_date && (
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Current Date:</span>{' '}
                        {new Date(selectedClaim.tentative_payment_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <span className="flex items-center gap-2">
                      <Calendar size={16} />
                      New Tentative Payment Date *
                    </span>
                  </label>
                  <input
                    type="date"
                    value={tentativePaymentDate}
                    onChange={(e) => setTentativePaymentDate(e.target.value)}
                    min={getTodayDate()}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    📧 An email notification will be sent to the employee
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleUpdatePaymentDate}
                    disabled={!tentativePaymentDate || updatePaymentDateLoading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-blue-300 disabled:to-blue-300 text-white py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
                  >
                    {updatePaymentDateLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Update Date
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowUpdatePaymentDateModal(false);
                      setTentativePaymentDate('');
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
                  >
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Claim Details Modal */}
        {showDetails && selectedClaim && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-white">Claim Details</h2>
                    <p className="text-blue-100 text-sm mt-1">Complete claim information and management</p>
                  </div>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-blue-100 hover:text-white transition-colors p-1"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <label className="block text-sm font-semibold text-gray-600 mb-2">
                        <span className="flex items-center gap-2">
                          <Users size={16} />
                          Employee
                        </span>
                      </label>
                      <p className="text-gray-900 font-medium text-lg">{selectedClaim.employee_name || selectedClaim.employee?.name}</p>
                      <p className="text-sm text-gray-500">{selectedClaim.employee_email || selectedClaim.employee?.email}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <label className="block text-sm font-semibold text-gray-600 mb-2">
                        <span className="flex items-center gap-2">
                          <FileText size={16} />
                          Purpose
                        </span>
                      </label>
                      <p className="text-gray-900 font-medium">{selectedClaim.purpose}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <label className="block text-sm font-semibold text-blue-700 mb-2">
                          <span className="flex items-center gap-2">
                            <DollarSign size={16} />
                            Amount
                          </span>
                        </label>
                        <p className="text-blue-900 font-bold text-xl">₹{Number(selectedClaim.amount || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <label className="block text-sm font-semibold text-gray-600 mb-2">Status</label>
                        <div className="mt-1">{getStatusBadge(selectedClaim.status)}</div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <label className="block text-sm font-semibold text-gray-600 mb-2">
                        <span className="flex items-center gap-2">
                          <Calendar size={16} />
                          Created Date
                        </span>
                      </label>
                      <p className="text-gray-900 font-medium">{new Date(selectedClaim.created_at).toLocaleString()}</p>
                    </div>

                    {/* Payment Date Display */}
                    {selectedClaim.tentative_payment_date && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <label className="block text-sm font-semibold text-green-700 mb-2">
                              <span className="flex items-center gap-2">
                                <Calendar size={16} />
                                Tentative Payment Date
                              </span>
                            </label>
                            <p className="text-green-900 font-bold text-lg">
                              {new Date(selectedClaim.tentative_payment_date).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                            {selectedClaim.payment_date_set_by_name && (
                              <p className="text-xs text-green-600 mt-1">
                                Set by {selectedClaim.payment_date_set_by_name}
                              </p>
                            )}
                          </div>
                          {/* Update button - now shows for HR, RMG, and Jerson */}
                          {canApproveReject && selectedClaim.status === 'APPROVED' && (
                            <button
                              onClick={() => {
                                setTentativePaymentDate(selectedClaim.tentative_payment_date || '');
                                setShowUpdatePaymentDateModal(true);
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                            >
                              <Edit size={12} />
                              Update
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Show button to set payment date if not set and claim is approved */}
                    {!selectedClaim.tentative_payment_date && selectedClaim.status === 'APPROVED' && canApproveReject && (
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-semibold text-yellow-800 mb-1">
                              Payment Date Not Set
                            </p>
                            <p className="text-xs text-yellow-600">
                              Set a tentative payment date for this claim
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setTentativePaymentDate('');
                              setShowUpdatePaymentDateModal(true);
                            }}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                          >
                            <Calendar size={12} />
                            Set Date
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Attachment Section */}
                    {selectedClaim.attachment && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <label className="block text-sm font-semibold text-blue-700 mb-2">
                          <span className="flex items-center gap-2">
                            <Paperclip size={16} />
                            Attachment
                          </span>
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="text-blue-900 font-medium text-sm">
                              {selectedClaim.attachment.split('/').pop() || 'View Attachment'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleFileAction(selectedClaim.attachment, 'claim-attachment')}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                            >
                              <ExternalLink size={14} />
                              View
                            </button>
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = selectedClaim.attachment;
                                link.download = 'claim-attachment';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                            >
                              <Download size={14} />
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <label className="block text-sm font-semibold text-gray-600 mb-2">
                        <span className="flex items-center gap-2">
                          <FileText size={16} />
                          One-line Description
                        </span>
                      </label>
                      <p className="text-gray-900">{selectedClaim.one_line_description}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border h-full">
                      <label className="block text-sm font-semibold text-gray-600 mb-2">
                        <span className="flex items-center gap-2">
                          <FileText size={16} />
                          Detailed Description
                        </span>
                      </label>
                      <div className="bg-white p-3 rounded border min-h-[150px]">
                        <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{selectedClaim.detailed_description}</p>
                      </div>
                    </div>

                    {selectedClaim.rejection_reason && (
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <label className="block text-sm font-semibold text-red-700 mb-2">
                          <span className="flex items-center gap-2">
                            <AlertTriangle size={16} />
                            Rejection Reason
                          </span>
                        </label>
                        <div className="bg-white p-3 rounded border border-red-200">
                          <p className="text-red-900">{selectedClaim.rejection_reason}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between pt-6 border-t border-gray-200">
                  <div className="flex gap-3">
                    {canApproveReject && selectedClaim.status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => {
                            setShowDetails(false);
                            showConfirmation('approve', selectedClaim);
                          }}
                          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-semibold"
                        >
                          <CheckCircle size={16} />
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setShowDetails(false);
                            setShowRejectModal(true);
                          }}
                          className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-semibold"
                        >
                          <XCircle size={16} />
                          Reject
                        </button>
                      </>
                    )}
                    {canApproveReject && selectedClaim.status === 'APPROVED' && (
                      <button
                        onClick={() => {
                          setShowDetails(false);
                          showConfirmation('close', selectedClaim);
                        }}
                        className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-semibold"
                      >
                        <XCircle size={16} />
                        Close
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2 font-semibold"
                  >
                    <Eye size={16} />
                    Close View
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && selectedClaim && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-white">Reject Claim</h3>
                    <p className="text-red-100 text-sm mt-1">Please provide a detailed reason</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectionReason('');
                    }}
                    className="text-red-100 hover:text-white transition-colors p-1"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-red-100 rounded-full">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Rejecting: {selectedClaim.purpose}
                    </h4>
                    <p className="text-sm text-gray-600">
                      Employee: {selectedClaim.employee?.name || selectedClaim.employee_name}
                    </p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a clear and detailed reason for rejecting this claim..."
                    rows={4}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleReject}
                    disabled={!rejectionReason.trim()}
                    className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-red-300 disabled:to-red-300 text-white px-4 py-3 rounded-lg transition-all font-semibold flex items-center justify-center gap-2"
                  >
                    <XCircle size={16} />
                    Reject Claim
                  </button>
                  <button
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectionReason('');
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-lg transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <X size={16} />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default AdminClaimManagement;